import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import API from '../utils/api';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

export default function ContactUs() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const subjectParam = params.get('subject');
    if (subjectParam) {
      setForm(prev => ({ ...prev, subject: subjectParam }));
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error('Please fill in all required fields.');
    }
    
    setLoading(true);
    try {
      const res = await API.post('/contact', form);
      toast.success(res.data.message || 'Your message has been sent successfully! Our team will get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <section className="hero" style={{ minHeight: '40vh', paddingTop: '140px', paddingBottom: '60px' }}>
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-3" />
        </div>
        <div className="container text-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="badge">📬 Get in Touch</span>
            <h1 className="hero-title" style={{ fontSize: '3.5rem', marginTop: 16 }}>
              Contact <span className="text-gradient">Us</span>
            </h1>
            <p className="hero-subtitle" style={{ margin: '0 auto', maxWidth: 600 }}>
              Have questions about our plans, need custom enterprise support, or just want to say hello? Our experts are here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 40 }}>
            {/* Contact Information */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }}
              style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
            >
              <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 16 }}>
                Let's start a <span className="text-gradient">conversation</span>
              </h2>
              
              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '24px' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                  📧
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: 16, color: 'var(--text-muted)' }}>Email Us</h4>
                  <a href="mailto:support@infravelox.com" style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>support@infravelox.com</a>
                </div>
              </div>

              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '24px' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                  📞
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: 16, color: 'var(--text-muted)' }}>Call Us</h4>
                  <a href="tel:+916354478749" style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>+91 6354478749</a>
                </div>
              </div>

              <div className="glass-card" style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '24px' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                  🏢
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: 16, color: 'var(--text-muted)' }}>Visit Us</h4>
                  <p style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', margin: 0, lineHeight: 1.5 }}>
                    401, 4th Floor, Ambience Complex, Commerce Six Rd, near Metro Station, Roop Nagar, Sarvottam Nagar Society, Navrangpura, Ahmedabad, Gujarat 380009
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <div className="glass-card" style={{ padding: '40px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24 }}>Send us a Message</h3>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="John Doe" 
                      value={form.name} 
                      onChange={e => setForm({...form, name: e.target.value})} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      placeholder="john@example.com" 
                      value={form.email} 
                      onChange={e => setForm({...form, email: e.target.value})} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="How can we help?" 
                      value={form.subject} 
                      onChange={e => setForm({...form, subject: e.target.value})} 
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea 
                      className="form-control" 
                      placeholder="Write your message here..." 
                      rows="5"
                      style={{ minHeight: '120px', resize: 'vertical' }}
                      value={form.message} 
                      onChange={e => setForm({...form, message: e.target.value})} 
                      required 
                    ></textarea>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ justifyContent: 'center', marginTop: 10 }}>
                    {loading ? '⏳ Sending...' : '🚀 Send Message'}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
